import React, { useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  Search,
  Download,
  User,
  Mail,
  Phone,
  Calendar,
  MessageSquare,
  Briefcase,
  FileText,
  AlertCircle,
  CheckCircle,
  Loader2,
  Shield,
  Database,
  Clock
} from 'lucide-react';

const DataExportPage = () => {
  const { token } = useAuth();
  const [searchEmail, setSearchEmail] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [exporting, setExporting] = useState(false);

  const searchUserData = async () => {
    if (!searchEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      setSearching(true);
      setSearchResults(null);
      
      const response = await axios.get(`${API}/data-export/search?email=${encodeURIComponent(searchEmail)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSearchResults(response.data);
      
      if (response.data.total_records === 0) {
        toast.info('No data found for this email address');
      } else {
        toast.success(`Found ${response.data.total_records} records`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const downloadPDF = async () => {
    if (!searchResults || searchResults.total_records === 0) return;

    try {
      setExporting(true);
      
      const response = await axios.get(
        `${API}/data-export/pdf?email=${encodeURIComponent(searchEmail)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `data_export_${searchEmail.replace('@', '_at_')}_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to generate PDF');
    } finally {
      setExporting(false);
    }
  };

  const downloadJSON = async () => {
    if (!searchResults || searchResults.total_records === 0) return;

    try {
      const response = await axios.get(
        `${API}/data-export/json?email=${encodeURIComponent(searchEmail)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `data_export_${searchEmail.replace('@', '_at_')}_${new Date().toISOString().slice(0,10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('JSON downloaded successfully');
    } catch (error) {
      toast.error('Failed to download JSON');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <Shield className="w-7 h-7 text-[#1F3C4F]" />
            Privacy Data Export
          </h1>
          <p className="text-slate-500 mt-1">
            Search and export user data for GDPR/CCPA compliance requests
          </p>
        </div>

        {/* Info Banner */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <Database className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900">Data Subject Access Request (DSAR) Tool</h3>
                <p className="text-blue-700 text-sm mt-1">
                  Use this tool to fulfill privacy requests. Enter an email address to find all associated 
                  data across contact submissions and other records. Export as PDF for formal requests.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Search Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Search User Data
            </CardTitle>
            <CardDescription>
              Enter an email address to find all data associated with that user
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <div className="flex-1">
                <Label htmlFor="email" className="sr-only">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter email address to search..."
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchUserData()}
                  className="text-lg py-6"
                />
              </div>
              <Button 
                onClick={searchUserData}
                disabled={searching}
                className="bg-[#1F3C4F] hover:bg-[#152a38] px-8"
              >
                {searching ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Search className="w-5 h-5 mr-2" />
                    Search
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results Section */}
        {searchResults && (
          <div className="space-y-4">
            {/* Summary Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {searchResults.total_records > 0 ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-yellow-600" />
                      )}
                      Search Results for: {searchEmail}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Found {searchResults.total_records} total records across {Object.keys(searchResults.data || {}).length} categories
                    </CardDescription>
                  </div>
                  {searchResults.total_records > 0 && (
                    <div className="flex gap-2">
                      <Button 
                        onClick={downloadJSON}
                        variant="outline"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Download JSON
                      </Button>
                      <Button 
                        onClick={downloadPDF}
                        disabled={exporting}
                        className="bg-[#1F3C4F] hover:bg-[#152a38]"
                      >
                        {exporting ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Download PDF Report
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
            </Card>

            {/* Contact Submissions */}
            {searchResults.data?.contact_submissions?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                    Contact Form Submissions ({searchResults.data.contact_submissions.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {searchResults.data.contact_submissions.map((submission, index) => (
                    <div 
                      key={index}
                      className="p-4 bg-slate-50 rounded-lg border space-y-3"
                    >
                      <div className="grid md:grid-cols-2 gap-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <span className="text-sm"><strong>Name:</strong> {submission.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <span className="text-sm"><strong>Email:</strong> {submission.email}</span>
                        </div>
                        {submission.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-slate-400" />
                            <span className="text-sm"><strong>Phone:</strong> {submission.phone}</span>
                          </div>
                        )}
                        {submission.company && (
                          <div className="flex items-center gap-2">
                            <Briefcase className="w-4 h-4 text-slate-400" />
                            <span className="text-sm"><strong>Company:</strong> {submission.company}</span>
                          </div>
                        )}
                      </div>
                      {submission.message && (
                        <div className="pt-2 border-t">
                          <p className="text-sm text-slate-600"><strong>Message:</strong></p>
                          <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{submission.message}</p>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-xs text-slate-500 pt-2 border-t">
                        <Calendar className="w-3 h-3" />
                        Submitted: {formatDate(submission.created_at || submission.submitted_at)}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Customers */}
            {searchResults.data?.customers?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <User className="w-5 h-5 text-green-600" />
                    Customer Records ({searchResults.data.customers.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {searchResults.data.customers.map((customer, index) => (
                    <div 
                      key={index}
                      className="p-4 bg-slate-50 rounded-lg border space-y-3"
                    >
                      <div className="grid md:grid-cols-2 gap-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <span className="text-sm"><strong>Name:</strong> {customer.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <span className="text-sm"><strong>Email:</strong> {customer.email}</span>
                        </div>
                        {customer.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-slate-400" />
                            <span className="text-sm"><strong>Phone:</strong> {customer.phone}</span>
                          </div>
                        )}
                        {customer.company && (
                          <div className="flex items-center gap-2">
                            <Briefcase className="w-4 h-4 text-slate-400" />
                            <span className="text-sm"><strong>Company:</strong> {customer.company}</span>
                          </div>
                        )}
                      </div>
                      {customer.address && (
                        <div className="text-sm text-slate-600">
                          <strong>Address:</strong> {customer.address}
                        </div>
                      )}
                      {customer.notes && (
                        <div className="text-sm text-slate-600">
                          <strong>Notes:</strong> {customer.notes}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-xs text-slate-500 pt-2 border-t">
                        <Clock className="w-3 h-3" />
                        Created: {formatDate(customer.created_at)}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* No Data Message */}
            {searchResults.total_records === 0 && (
              <Card className="border-yellow-200 bg-yellow-50">
                <CardContent className="p-6 text-center">
                  <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-yellow-900">No Data Found</h3>
                  <p className="text-yellow-700 mt-1">
                    No records were found associated with the email address: <strong>{searchEmail}</strong>
                  </p>
                  <p className="text-sm text-yellow-600 mt-2">
                    This means either no data exists for this user, or the email address may be incorrect.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DataExportPage;
